import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileToBase64 } from "@/lib/api";
import { useSession } from "@/lib/session";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ScanLine, Receipt, AlertCircle, Lock, Tag, AlertTriangle, Check, Mic, Square, Camera, Upload } from "lucide-react";
import CurrentShiftSummary from "@/components/CurrentShiftSummary";
import { useT } from "@/lib/i18n";

const NOTES = [200, 100, 50, 20, 10, 5];
const COINS = [2, 1, 0.5, 0.2, 0.1, 0.05];

export default function EndShift() {
  const { shift, setShift, lockPortal, lockLiveView, pendingDraft, voucherPreview } = useSession();
  const nav = useNavigate();
  const t = useT();
  const [noteQty, setNoteQty] = useState({});
  const [coinQty, setCoinQty] = useState({});
  const [extras, setExtras] = useState({
    visa_total: "", wolt_total: "", foody_total: "", bolt_total: "", kronos_total: "", lacheia_total: "",
  });
  // Live Wolt sales — auto-populated from webhook ingest. When > 0, the
  // Wolt input is LOCKED and the value is read-only.
  const [liveWolt, setLiveWolt] = useState({ total: 0, count: 0, locked: false });
  const [liveBolt, setLiveBolt] = useState({ total: 0, count: 0, locked: false });
  const [liveFoody, setLiveFoody] = useState({ total: 0, count: 0, locked: false });
  const [expected, setExpected] = useState(null);
  const [zReport, setZReport] = useState(null);
  const [visaSlip, setVisaSlip] = useState(null);
  const [scanning, setScanning] = useState("");
  const [closing, setClosing] = useState(false);
  const [shiftInvoices, setShiftInvoices] = useState([]);
  // Backend-issued blockers (UNPAID invoices, cheque leaks…) gating CLOSE.
  const [preflight, setPreflight] = useState({ can_close: true, blockers: [], pending_count: 0 });
  // Iter 29 · Pipeline 2 — Staff handover dropdown. Lists active roster
  // pulled from /api/staff so the operator closing the shift can re-attribute
  // it from the originator (e.g. Giannos) to themselves (e.g. Maria). The
  // Twilio summary + audit ledger pick up the new name automatically.
  const [staff, setStaff] = useState([]);
  const [handoverBusy, setHandoverBusy] = useState(false);
  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data?.staff || [])).catch(() => setStaff([]));
  }, []);
  const handoverTo = async (newName) => {
    if (!shift || !newName || newName === shift.employee_name) return;
    setHandoverBusy(true);
    try {
      const { data } = await api.patch(`/shifts/${shift.id}`, { employee_name: newName });
      setShift(data);
      toast.success(`Handover → ${newName}`);
    } catch {
      toast.error("Handover failed");
    } finally {
      setHandoverBusy(false);
    }
  };

  useEffect(() => {
    if (!shift) return;
    api.get(`/shifts/${shift.id}/expected`).then((r) => setExpected(r.data)).catch(() => {});
    api.get(`/shift-invoices`, { params: { shift_id: shift.id } })
      .then((r) => setShiftInvoices(r.data))
      .catch(() => {});

    // Preflight — backend gate for UNPAID invoices and any cheque leaks.
    const loadPreflight = () => {
      api.get(`/shifts/${shift.id}/preflight`)
        .then((r) => setPreflight(r.data))
        .catch(() => setPreflight({ can_close: true, blockers: [], pending_count: 0 }));
    };
    loadPreflight();
    const pi = setInterval(loadPreflight, 5000);

    // Live Wolt poll — pulls live_wolt_sales every 5 s from the webhook
    // accumulator. Locks the manual Wolt input as soon as the total > 0.
    const pollWolt = async () => {
      try {
        const { data } = await api.get(`/shifts/${shift.id}/wolt-live`);
        const total = Number(data.live_wolt_sales) || 0;
        setLiveWolt({ total, count: data.order_count || 0, locked: total > 0 });
        if (total > 0) {
          setExtras((p) => ({ ...p, wolt_total: total.toFixed(2) }));
        }
      } catch {
        /* keep manual entry path open */
      }
    };
    const pollBolt = async () => {
      try {
        const { data } = await api.get(`/shifts/${shift.id}/bolt-live`);
        const total = Number(data.live_bolt_sales) || 0;
        setLiveBolt({ total, count: data.order_count || 0, locked: total > 0 });
        if (total > 0) {
          setExtras((p) => ({ ...p, bolt_total: total.toFixed(2) }));
        }
      } catch {
        /* keep manual entry path open */
      }
    };
    const pollFoody = async () => {
      try {
        const { data } = await api.get(`/shifts/${shift.id}/foody-live`);
        const total = Number(data.live_foody_sales) || 0;
        setLiveFoody({ total, count: data.order_count || 0, locked: total > 0 });
        if (total > 0) {
          setExtras((p) => ({ ...p, foody_total: total.toFixed(2) }));
        }
      } catch {
        /* keep manual entry path open */
      }
    };
    pollWolt();
    pollBolt();
    pollFoody();
    const i = setInterval(() => { pollWolt(); pollBolt(); pollFoody(); }, 5000);
    return () => { clearInterval(i); clearInterval(pi); };
  }, [shift?.id]);

  const countedCash = useMemo(() => {
    let total = 0;
    NOTES.forEach((d) => { total += d * (Number(noteQty[d]) || 0); });
    COINS.forEach((d) => { total += d * (Number(coinQty[d]) || 0); });
    return Number(total.toFixed(2));
  }, [noteQty, coinQty]);

  const expectedTotal = useMemo(() => {
    if (!expected) return 0;
    // Iter 22 — Expected Cash = cash retail sales (Z-Report)
    //                          + live delivery (Wolt + Bolt + Foody, webhook-fed)
    //                          - validated cash invoices
    //                          - drawer payouts & cash drops
    // The legacy €700 starting-float baseline has been removed; the ledger
    // initialises strictly from 0.00 and tracks pure session inputs.
    const cash_sales = zReport?.cash_total || 0;
    const delivery_live =
      (liveWolt?.total  || 0) +
      (liveBolt?.total  || 0) +
      (liveFoody?.total || 0);
    return Number(
      (
        cash_sales +
        delivery_live -
        expected.payouts -
        expected.cash_drops -
        expected.cash_invoices_paid
      ).toFixed(2)
    );
  }, [expected, zReport, liveWolt, liveBolt, liveFoody]);

  const discrepancy = Number((countedCash - expectedTotal).toFixed(2));

  // Iter 79 — JCC / Viva bank reconciliation. Loads the verified daily
  // settled card volume from the gateway and renders an anti-fraud audit
  // row. Silent when the integration is OFF for this tenant.
  const [bankSettled, setBankSettled] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get("/jcc-viva/bank-settled")
        .then(({ data }) => { if (alive) setBankSettled(data); })
        .catch(() => { /* fail-soft */ });
    };
    load();
    const tid = setInterval(load, 30000);
    return () => { alive = false; clearInterval(tid); };
  }, []);
  const visaReported = Number(extras.visa_total) || 0;
  const bankAmount = Number(bankSettled?.bank_amount || 0);
  const bankDiff = Number((visaReported - bankAmount).toFixed(2));

  const onScan = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setScanning(kind);
    const t = toast.loading(`Scanning ${kind === "zreport" ? "Z-Report" : "VISA Slip"}…`);
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/ocr/scan", {
        image_base64: b64, shift_id: shift.id, location: shift.location, kind,
      });
      if (kind === "zreport") setZReport(data.data);
      else {
        setVisaSlip(data.data);
        setExtras((p) => ({ ...p, visa_total: data.data?.total_amount || p.visa_total }));
      }
      toast.success(`${kind === "zreport" ? "Z-Report" : "VISA"} parsed`, { id: t });
    } catch (err) {
      toast.error("Scan failed", { id: t });
    } finally {
      setScanning("");
    }
  };

  // Iter 82 — Chained "Report → Archive → Reset" workflow.
  // The GREEN WhatsApp button is now the primary gatekeeper. The red
  // "SAVE EVERYTHING & CLOSE" button is kept as a manual override
  // fallback only — disabled by default in the chained mode.
  //
  // Sequence:
  //   1. Close the shift (POST /shifts/{id}/end)  → archives every
  //      figure + photo into the active store's daily subfolder
  //      via the existing Iter 80 daily-folders grouping.
  //   2. Fetch the freshly-built EN/EL Z-Report summary + media URLs
  //      from /endshift/{id}/whatsapp-link.
  //   3. Open native WhatsApp via navigator.share({text,files}) when
  //      supported, else window.open(wa.me text-only deep-link).
  //   4. Wipe in-memory state + lock owner cookies + redirect to
  //      /login so the next operator starts on a clean slate.
  const chainedSendCloseLogout = async () => {
    if (!shift) return;
    const blockers = (preflight.blockers || []).filter((b) => b.severity !== "info");
    if (voucherPreview || pendingDraft || blockers.length > 0) {
      // Iter 91 — Hard-core security guard with the exact bilingual
      // Greek warning string the user spec mandates.
      toast.error(
        "❌ ΣΦΑΛΜΑ ΑΣΦΑΛΕΙΑΣ: Πρέπει να καταχωρήσετε πρώτα όλα τα Τιμολόγια, Έξοδα και Αποδείξεις της βάρδιας (Add to Shift). Το End Shift είναι κλειδωμένο!",
        { duration: 8000, id: "endshift-iter91-guard" }
      );
      return;
    }
    setClosing(true);
    const closingShiftId = shift.id;
    try {
      // Step 1+2 — close the shift first so end_data is persisted.
      await api.post(`/shifts/${closingShiftId}/end`, {
        notes: noteQty,
        coins: coinQty,
        visa_total: Number(extras.visa_total) || 0,
        wolt_total: Number(extras.wolt_total) || 0,
        foody_total: Number(extras.foody_total) || 0,
        bolt_total: Number(extras.bolt_total) || 0,
        kronos_total: Number(extras.kronos_total) || 0,
        lacheia_total: Number(extras.lacheia_total) || 0,
        expected_cash: expectedTotal,
        counted_cash: countedCash,
        discrepancy,
      });
      toast.success(`📁 ${shift.location} · archived to daily folder`);

      // Step 1 (in user spec) — fire the WhatsApp deep-link with
      // bundled text + photos. We do this AFTER the close so the
      // payload reflects the freshly-archived totals.
      try {
        // Iter 92 — Step A · Compile the formal end-shift PDF on the
        // server. The endpoint stitches every figure + scanned photo into
        // a single PDF, uploads it to object storage, mints a short_id,
        // and returns the short URL we'll embed in the WhatsApp message.
        // If PDF compile fails we degrade gracefully to the Iter 90/91
        // free-text + clipboard fallback.
        let chained = null;
        try {
          const { data: pdfData } = await api.post(
            `/endshift/${closingShiftId}/pdf`,
            {}
          );
          if (pdfData?.short_url) {
            chained = pdfData;
            toast.success(
              `📄 PDF compiled · ${(pdfData.size_bytes / 1024).toFixed(0)} KB · short link ready`
            );
          }
        } catch (pdfErr) {
          console.warn("Iter 92 PDF compile failed, falling back", pdfErr);
        }

        // Pull the bilingual summary + media_urls from the legacy
        // whatsapp-link endpoint so the clipboard fallback still
        // contains the full Z-Report text body even when the PDF
        // succeeds. wa_url is replaced by the Iter 92 short-link URL
        // when the PDF compile succeeded.
        const { data } = await api.get(`/endshift/${closingShiftId}/whatsapp-link`);
        const effectiveWaUrl = chained?.wa_url || data?.wa_url;
        const effectiveSummary = chained?.summary || data?.summary || "";
        if (effectiveWaUrl) {
          const mediaUrls = Array.isArray(data.media_urls) ? data.media_urls : [];

          // Iter 90/91/92 — 100% FREE clipboard + local download bridge.
          // (1) Write the full summary + PDF short link to clipboard FIRST so
          //     the operator always has it ready to paste even if focus changes.
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              const clipboardBody = chained?.short_url
                ? `${effectiveSummary}\n\n📄 ${chained.short_url}`
                : effectiveSummary;
              await navigator.clipboard.writeText(clipboardBody);
              toast.success("📋 Summary + PDF link copied · paste it in WhatsApp");
            }
          } catch {
            /* clipboard may be denied — fail-soft, WA link still opens */
          }

          // Iter 91 — Open WhatsApp IMMEDIATELY (before the long media
          // download loop) so the OS focus transition happens while the
          // browser is still alive. Photo downloads queue in the
          // background and survive focus changes because they fire from
          // a separate Promise.
          let nativeShared = false;
          if (
            !chained?.short_url &&
            navigator.share &&
            typeof navigator.canShare === "function" &&
            mediaUrls.length > 0
          ) {
            try {
              const files = [];
              for (let i = 0; i < mediaUrls.length; i += 1) {
                const absolute = /^https?:/.test(mediaUrls[i])
                  ? mediaUrls[i]
                  : `${process.env.REACT_APP_BACKEND_URL || ""}${mediaUrls[i]}`;
                const res = await fetch(absolute, { credentials: "omit" });
                if (!res.ok) continue;
                const blob = await res.blob();
                const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
                files.push(new File([blob], `kioskforce-shift-${i + 1}.${ext}`, {
                  type: blob.type || "image/jpeg",
                }));
              }
              if (files.length && navigator.canShare({ files })) {
                await navigator.share({
                  title: "KioskForce ERP · End Shift",
                  text: effectiveSummary,
                  files,
                });
                nativeShared = true;
                toast.success(`📤 ${files.length} photo(s) + summary sent to WhatsApp`);
              }
            } catch (shareErr) {
              if (shareErr && shareErr.name !== "AbortError") {
                toast.info("Native share unavailable · opening WhatsApp text-only");
              }
            }
          }
          if (!nativeShared) {
            // Iter 96 — wrap the OS-level window.open in a silent
            // try/catch. Mobile Safari + locked-down browsers can throw
            // SecurityError or simply return null when a popup is
            // blocked — neither must surface a user-facing warning when
            // the backend has already returned a 200 for the shift
            // archive. The clipboard payload + short URL is the safety
            // net the operator always has.
            let waLaunched = false;
            try {
              const waWin = window.open(effectiveWaUrl, "_blank", "noopener,noreferrer");
              waLaunched = !!waWin;
            } catch (openErr) {
              if (typeof console !== "undefined" && console.debug) {
                console.debug("[Iter 96] window.open blocked:", openErr);
              }
            }
            if (chained?.short_url) {
              toast.success(
                waLaunched
                  ? "🔗 WhatsApp opened with PDF short link · photos embedded in the PDF"
                  : "✓ PDF short link copied · open WhatsApp and paste when ready"
              );
            } else if (mediaUrls.length > 0 && waLaunched) {
              // Iter 91 — Queue the photo downloads AFTER the WhatsApp tab
              // opens (focus has already shifted to the new tab/app). Run
              // them in a fire-and-forget Promise so focus loss can never
              // interrupt the wipe-and-logout step that follows.
              (async () => {
                try {
                  for (let i = 0; i < mediaUrls.length; i += 1) {
                    const absolute = /^https?:/.test(mediaUrls[i])
                      ? mediaUrls[i]
                      : `${process.env.REACT_APP_BACKEND_URL || ""}${mediaUrls[i]}`;
                    const res = await fetch(absolute, { credentials: "omit" });
                    if (!res.ok) continue;
                    const blob = await res.blob();
                    const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = `kioskforce-shift-${i + 1}.${ext}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => URL.revokeObjectURL(link.href), 4000);
                  }
                  toast.info(`📥 ${mediaUrls.length} photo(s) downloaded · attach in WhatsApp`);
                } catch {
                  /* download blocked — operator still has the URL list */
                }
              })();
            }
          }
        }
      } catch (waErr) {
        // Iter 96 — Graceful Fallback. The shift is already archived
        // (HTTP 200 from /shifts/{id}/end above), so a delayed/blocked
        // OS application-switch popup must NEVER surface an amber
        // warning to the operator. We log the underlying error for
        // ops/dev triage but show a calm success state instead.
        if (typeof console !== "undefined" && console.debug) {
          console.debug("[Iter 96] WhatsApp launcher fallback:", waErr);
        }
        toast.success("✓ Shift archived · paste the summary in WhatsApp when ready", {
          id: "endshift-iter96-archived",
        });
      }

      // Step 3 — total screen reset and operator logout.
      setNoteQty({});
      setCoinQty({});
      setExtras({ visa_total: "", wolt_total: "", foody_total: "", bolt_total: "", kronos_total: "", lacheia_total: "" });
      setZReport(null);
      setVisaSlip(null);
      setExpected(null);
      setShiftInvoices([]);
      setShift(null);
      try { lockPortal(); lockLiveView(); } catch { /* noop */ }
      setTimeout(() => nav("/login", { replace: true }), 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chained close failed");
    } finally {
      setClosing(false);
    }
  };

  const saveAndClose = async () => {
    if (!shift) return;
    // Guard rail: block close if any pending draft survives.
    const blockers = (preflight.blockers || []).filter((b) => b.severity !== "info");
    if (voucherPreview || pendingDraft || blockers.length > 0) {
      toast.error(
        "❌ ΣΦΑΛΜΑ ΑΣΦΑΛΕΙΑΣ: Πρέπει να καταχωρήσετε πρώτα όλα τα Τιμολόγια, Έξοδα και Αποδείξεις της βάρδιας (Add to Shift). Το End Shift είναι κλειδωμένο!",
        { duration: 8000, id: "endshift-iter91-manual-guard" }
      );
      return;
    }
    setClosing(true);
    try {
      await api.post(`/shifts/${shift.id}/end`, {
        notes: noteQty,
        coins: coinQty,
        visa_total: Number(extras.visa_total) || 0,
        wolt_total: Number(extras.wolt_total) || 0,
        foody_total: Number(extras.foody_total) || 0,
        bolt_total: Number(extras.bolt_total) || 0,
        kronos_total: Number(extras.kronos_total) || 0,
        lacheia_total: Number(extras.lacheia_total) || 0,
        expected_cash: expectedTotal,
        counted_cash: countedCash,
        discrepancy,
      });
      toast.success(`${shift.employee_name} → archived. Resetting register…`);
      // --- Atomic Master Close: hard-reset of THIS register's state -----
      // (other tablets / kiosks keep their localStorage untouched).
      setNoteQty({});
      setCoinQty({});
      setExtras({ visa_total: "", wolt_total: "", foody_total: "", bolt_total: "", kronos_total: "", lacheia_total: "" });
      setZReport(null);
      setVisaSlip(null);
      setExpected(null);
      setShiftInvoices([]);
      setShift(null);
      // Clear Owner-context tokens so the next employee starts at the
      // savvaspos.store/login gateway with a clean slate.
      try { lockPortal(); lockLiveView(); } catch { /* noop */ }
      // Replace history entry so the browser back-button can't return to
      // a closed shift (HTTPS-only on production).
      setTimeout(() => nav("/login", { replace: true }), 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close failed");
    } finally {
      setClosing(false);
    }
  };

  if (!shift) {
    return (
      <Card className="p-12 max-w-xl mx-auto mt-12 text-center border-dashed border-2" data-testid="end-shift-no-shift">
        <AlertCircle className="h-10 w-10 mx-auto text-slate-400 mb-3" />
        <p className="text-slate-600">No active shift. Please start one from <b>NEW LOGIN</b>.</p>
      </Card>
    );
  }

  // Combined client + server draft gate. ANY of these blocks SAVE & CLOSE:
  //   • voucherPreview (cashflow draft staged on Invoices preview dialog)
  //   • pendingDraft (OCR invoice scan extracted but not committed)
  //   • backend preflight blockers (UNPAID invoices on shift)
  const hardBlockers = (preflight.blockers || []).filter((b) => b.severity !== "info");
  const hasClientDraft = !!voucherPreview || pendingDraft;
  const hasPendingDrafts = hasClientDraft || hardBlockers.length > 0;
  const canClose = !hasPendingDrafts;

  return (
    <div className="pb-12">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">{t("endShift.title")}</h1>
          <div className="text-slate-500 mt-1 tracking-wide flex items-center gap-2 flex-wrap">
            <span>{shift.location} · Register {shift.register}</span>
            <span className="text-slate-400">·</span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
              Closing as
            </span>
            <select
              value={shift.employee_name || ""}
              onChange={(e) => handoverTo(e.target.value)}
              disabled={handoverBusy}
              data-testid="endshift-employee-select"
              className="bg-white border-2 border-slate-300 hover:border-slate-900 focus:border-slate-900 rounded-md px-2 py-1 text-sm font-bold text-slate-900 tracking-wide cursor-pointer transition-colors disabled:opacity-50"
            >
              {/* Always include the current name first so the select is never blank. */}
              {shift.employee_name && !staff.includes(shift.employee_name) && (
                <option value={shift.employee_name}>{shift.employee_name}</option>
              )}
              {staff.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <Stat label={t("endShift.expected")} value={`€${expectedTotal.toFixed(2)}`} accent="text-slate-700" testId="expected-cash" />
          <Stat label={t("endShift.counted")} value={`€${countedCash.toFixed(2)}`} accent="text-slate-900" testId="counted-cash" />
          <Stat
            label={t("endShift.discrepancy")}
            value={`${discrepancy >= 0 ? "+" : ""}€${discrepancy.toFixed(2)}`}
            accent={Math.abs(discrepancy) < 0.01 ? "text-emerald-700" : discrepancy < 0 ? "text-rose-700" : "text-amber-700"}
            testId="discrepancy"
          />
        </div>
      </header>

      {hasPendingDrafts && (
        <Card
          className="mb-6 p-5 border-2 border-amber-400 bg-amber-50 shadow-amber-200/40 shadow-md"
          data-testid="pending-drafts-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-7 w-7 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div
                className="font-bold text-amber-900 text-lg leading-tight"
                data-testid="banner-title"
              >
                {t("banner.title.primary")}
              </div>

              {hasClientDraft && (
                <div className="mt-3 rounded-md bg-white/70 border border-amber-300 px-3 py-2 text-[12px] text-amber-900" data-testid="banner-client-draft">
                  {voucherPreview ? t("banner.clientDraft.voucher") : t("banner.clientDraft.ocr")}
                </div>
              )}

              {hardBlockers.map((b, idx) => (
                <div
                  key={idx}
                  className="mt-3 rounded-md bg-white/70 border border-amber-300 px-3 py-2 text-[12px] text-amber-900"
                  data-testid={`banner-blocker-${b.kind}`}
                >
                  <div className="font-semibold">• {b.label_en}</div>
                  <div className="text-amber-700 italic">{b.label_el}</div>
                  {b.items && b.items.length > 0 && (
                    <ul className="mt-1 ml-3 list-disc text-[11px]">
                      {b.items.slice(0, 5).map((it) => (
                        <li key={it.id}>
                          {it.supplier_name || "Unknown"} · €{Number(it.total_amount || 0).toFixed(2)}
                        </li>
                      ))}
                      {b.items.length > 5 && <li>… +{b.items.length - 5} more</li>}
                    </ul>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={() => nav("/invoices")}
                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-bold uppercase tracking-wider transition-colors"
                data-testid="banner-go-to-invoices"
              >
                {t("banner.goToInvoices")}
              </button>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <h2 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-4">Cash Denomination Counter · Καταμέτρηση</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2 font-bold">Notes / Χαρτονομίσματα</div>
              <div className="space-y-2">
                {NOTES.map((d) => (
                  <DenomRow
                    key={d}
                    label={`€${d}`}
                    value={noteQty[d] ?? ""}
                    onChange={(v) => setNoteQty((p) => ({ ...p, [d]: v }))}
                    testId={`note-${d}`}
                    subtotal={(d * (Number(noteQty[d]) || 0)).toFixed(2)}
                  />
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2 font-bold">Coins / Κέρματα</div>
              <div className="space-y-2">
                {COINS.map((d) => (
                  <DenomRow
                    key={d}
                    label={d >= 1 ? `€${d}` : `${(d * 100).toFixed(0)}c`}
                    value={coinQty[d] ?? ""}
                    onChange={(v) => setCoinQty((p) => ({ ...p, [d]: v }))}
                    testId={`coin-${String(d).replace(".", "-")}`}
                    subtotal={(d * (Number(coinQty[d]) || 0)).toFixed(2)}
                  />
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-4">Other Totals · Λοιπά Σύνολα</h2>
          <div className="grid grid-cols-2 gap-3">
            <ExtraInput label="VISA / Card" v={extras.visa_total} setV={(v) => setExtras({ ...extras, visa_total: v })} testId="extra-visa" />
            <ExtraInput
              label={liveWolt.locked ? `Wolt · LIVE 🟢 (${liveWolt.count})` : "Wolt"}
              v={extras.wolt_total}
              setV={(v) => setExtras({ ...extras, wolt_total: v })}
              testId="extra-wolt"
              locked={liveWolt.locked}
            />
            <ExtraInput
              label={liveBolt.locked ? `Bolt · LIVE 🟢 (${liveBolt.count})` : "Bolt"}
              v={extras.bolt_total}
              setV={(v) => setExtras({ ...extras, bolt_total: v })}
              testId="extra-bolt"
              locked={liveBolt.locked}
            />
            <ExtraInput
              label="ΚΡΟΝΟΣ / KRONOS"
              v={extras.kronos_total}
              setV={(v) => setExtras({ ...extras, kronos_total: v })}
              testId="extra-kronos"
            />
            <ExtraInput
              label={liveFoody.locked ? `Foody · LIVE 🟢 (${liveFoody.count})` : "Foody"}
              v={extras.foody_total}
              setV={(v) => setExtras({ ...extras, foody_total: v })}
              testId="extra-foody"
              locked={liveFoody.locked}
            />
            <ExtraInput label="Λαχεία" v={extras.lacheia_total} setV={(v) => setExtras({ ...extras, lacheia_total: v })} testId="extra-lacheia" />
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <ScanBox
              label="Scan Z-Report"
              icon={<Receipt className="h-5 w-5" />}
              onPick={(e) => onScan(e, "zreport")}
              parsed={zReport && `Cash: €${(zReport.cash_total || 0).toFixed(2)} · Card: €${(zReport.card_total || 0).toFixed(2)}`}
              busy={scanning === "zreport"}
              testId="scan-zreport"
            />
            <ScanBox
              label="Scan VISA Slip"
              icon={<ScanLine className="h-5 w-5" />}
              onPick={(e) => onScan(e, "visa")}
              parsed={visaSlip && `Total: €${(visaSlip.total_amount || 0).toFixed(2)}`}
              busy={scanning === "visa"}
            />
          </div>
        </Card>
      </div>

      {/* Multi-Delivery Sync & SMS Alerts */}
      <Card className="p-6 border-2 border-cyan-200 bg-cyan-50/50 mb-6" data-testid="delivery-sync-card">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-5 w-5 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500" />
          <h2 className="text-lg font-bold tracking-tight">End Shift Multi-Delivery Sync · Σύγχρονη Παράδοση</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="border border-cyan-300 rounded-lg p-3 bg-white">
            <div className="text-xs font-semibold text-slate-600 uppercase">Wolt Webhook Sync</div>
            <div className="mt-2 text-2xl font-bold text-cyan-700">€{liveWolt.total.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">{liveWolt.count} orders • {liveWolt.locked ? '🟢 LOCKED' : 'manual entry'}</div>
          </div>
          <div className="border border-orange-300 rounded-lg p-3 bg-white">
            <div className="text-xs font-semibold text-slate-600 uppercase">Bolt Webhook Sync</div>
            <div className="mt-2 text-2xl font-bold text-orange-700">€{liveBolt.total.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">{liveBolt.count} orders • {liveBolt.locked ? '🟢 LOCKED' : 'manual entry'}</div>
          </div>
          <div className="border border-purple-300 rounded-lg p-3 bg-white">
            <div className="text-xs font-semibold text-slate-600 uppercase">Foody Webhook Sync</div>
            <div className="mt-2 text-2xl font-bold text-purple-700">€{liveFoody.total.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">{liveFoody.count} orders • {liveFoody.locked ? '🟢 LOCKED' : 'manual entry'}</div>
          </div>
        </div>
        <div className="text-xs text-slate-600 italic">
          ✓ Webhook endpoints (`/api/v1/wolt-webhook`, `/api/v1/bolt-webhook`, `/api/v1/foody-webhook`) auto-populate and lock delivery totals in real-time.
        </div>
      </Card>

      {/* Iter 79 · Bank Reconciliation Audit (JCC / Viva Wallet) */}
      {bankSettled?.enabled && (
        <Card
          className="p-6 border-2 border-indigo-300 bg-gradient-to-br from-indigo-50/60 via-white to-violet-50/40 mb-6"
          data-testid="endshift-bank-reconciliation-card"
        >
          <div className="flex items-center gap-2 mb-3">
            <Lock className="h-5 w-5 text-indigo-700" />
            <h2 className="text-lg font-bold tracking-tight text-slate-900">
              🔒 ΑΛΗΘΙΝΕΣ ΕΙΣΠΡΑΞΕΙΣ ΤΡΑΠΕΖΑΣ (JCC / VIVA)
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div
              className="rounded-lg bg-white ring-1 ring-indigo-200 p-4"
              data-testid="endshift-bank-volume"
            >
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Verified {bankSettled?.provider === "viva_wallet" ? "Viva Wallet" : (bankSettled?.provider || "JCC")} Card Volume
              </div>
              <div className="mt-1 text-2xl font-bold text-indigo-800">
                €{bankAmount.toFixed(2)}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {bankSettled?.count || 0} settled txns · {bankSettled?.day || "today"}
              </div>
            </div>
            <div
              className={`rounded-lg ring-1 p-4 ${
                Math.abs(bankDiff) > 0.01
                  ? "bg-rose-50 ring-rose-300"
                  : "bg-emerald-50 ring-emerald-300"
              }`}
              data-testid="endshift-bank-discrepancy"
            >
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Cash Balance Discrepancy · Ταμειακή Τρύπα
              </div>
              <div
                className={`mt-1 text-2xl font-bold ${
                  Math.abs(bankDiff) > 0.01 ? "text-rose-700" : "text-emerald-700"
                }`}
              >
                {bankDiff > 0 ? "+" : ""}€{bankDiff.toFixed(2)}
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Reported VISA €{visaReported.toFixed(2)} − Bank €{bankAmount.toFixed(2)}
              </div>
            </div>
          </div>
          {bankSettled?.mocked && (
            <p className="text-[11px] text-amber-700 mt-3">
              ⚠️ Live bank feed pending — showing audit proxy from same-day PAID·VISA invoices.
            </p>
          )}
        </Card>
      )}

      {/* SMS Alerts Notice */}
      <Card className="p-4 border border-emerald-300 bg-emerald-50/50 mb-6" data-testid="sms-alert-card">
        <div className="flex items-start gap-3">
          <div className="text-2xl">📞</div>
          <div className="flex-1">
            <div className="font-semibold text-emerald-900">Τηλέφωνο Ειδοποιήσεων SMS · SMS Alert Phone Configured</div>
            <div className="text-sm text-emerald-800 mt-1">
              Upon clicking &quot;SAVE EVERYTHING &amp; CLOSE&quot;, an automated SMS summary will be sent to the configured owner phone with:
            </div>
            <ul className="text-xs text-emerald-700 mt-2 ml-4 space-y-1 list-disc">
              <li>💵 Counted Cash: €{countedCash.toFixed(2)}</li>
              <li>💳 VISA / Cards: €{(Number(extras.visa_total) || 0).toFixed(2)}</li>
              <li>🚗 Deliveries (Wolt+Bolt+Foody): €{(liveWolt.total + liveBolt.total + liveFoody.total).toFixed(2)}</li>
              <li>📊 Discrepancy: €{discrepancy.toFixed(2)}</li>
            </ul>
          </div>
        </div>
      </Card>

      <CurrentShiftSummary shift={shift} refreshKey={shiftInvoices.length} />

      <PriceAuditPanel shift={shift} items={shiftInvoices} zReport={zReport} />

      {/* Iter 49 — Voice note + extra register photo (Twilio media bundle) */}
      <EndShiftMediaCapture shiftId={shift?.id} />

      {/* Iter 82 — Green WhatsApp button is now the PRIMARY gatekeeper.
          Clicking it chains: archive → WhatsApp → wipe & logout. */}
      <Button
        onClick={chainedSendCloseLogout}
        disabled={closing || !canClose}
        data-testid="endshift-chain-send-btn"
        title="Closes the shift, archives to the daily folder, opens WhatsApp pre-filled with the full Z-Report + photos, then logs out"
        className="w-full min-h-[96px] bg-emerald-600 hover:bg-emerald-700 text-white text-xl sm:text-2xl font-bold uppercase tracking-[0.18em] rounded-xl shadow-lg flex flex-col items-center justify-center gap-1 disabled:bg-slate-400 disabled:cursor-not-allowed disabled:opacity-80"
      >
        <span className="flex items-center gap-2">
          <span aria-hidden>🟢</span>
          <span>{closing ? "ARCHIVING · OPENING WHATSAPP…" : "ΑΠΟΣΤΟΛΗ ΜΕΣΩ WHATSAPP · SEND VIA WHATSAPP APP"}</span>
        </span>
        <span className="text-[11px] opacity-90 normal-case tracking-wide">
          1️⃣ Archive to {shift?.location || "store"} folder · 2️⃣ Open WhatsApp · 3️⃣ Wipe + logout
        </span>
      </Button>

      {/* Iter 82 — Red "SAVE EVERYTHING & CLOSE" is now a SECONDARY
          manual fallback (used only when WhatsApp is unavailable). */}
      <details className="rounded-lg ring-1 ring-slate-200 bg-slate-50/60" data-testid="endshift-manual-close-details">
        <summary className="cursor-pointer px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900">
          Manual close fallback (no WhatsApp) ▾
        </summary>
        <div className="p-3">
          <Button
            onClick={saveAndClose}
            disabled={closing || !canClose}
            data-testid="save-close-btn"
            className="w-full min-h-[72px] bg-red-600 hover:bg-red-700 text-white text-lg font-bold uppercase tracking-[0.18em] rounded-xl shadow flex flex-col items-center justify-center gap-1 disabled:bg-slate-400 disabled:cursor-not-allowed disabled:opacity-80"
          >
            <span>{closing ? t("endShift.archiving") : !canClose ? t("endShift.blocked") : t("endShift.saveAndClose")}</span>
            <span className="text-[10px] opacity-80 normal-case tracking-wide">
              Archives + logs out · skips WhatsApp dispatch
            </span>
          </Button>
        </div>
      </details>

      {/* Iter 68 — Legacy text-only WhatsApp deep-link button (kept for
          discoverability inside the manual fallback section). */}
      <details className="rounded-lg ring-1 ring-slate-200 bg-slate-50/60" data-testid="endshift-legacy-whatsapp-details">
        <summary className="cursor-pointer px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900">
          Send WhatsApp text only (no archive) ▾
        </summary>
        <div className="p-3">
          <WhatsAppDeepLinkButton shiftId={shift?.id} disabled={!canClose} />
        </div>
      </details>

      {/* Signature */}
      <div className="fixed bottom-4 right-6 text-xs text-slate-400 pointer-events-none font-mono">
        Made by John
      </div>
    </div>
  );
}

function DenomRow({ label, value, onChange, testId, subtotal }) {
  return (
    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
      <div className="w-12 font-mono text-base font-bold text-slate-700 text-right">{label}</div>
      <Input
        type="number"
        inputMode="numeric"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        data-testid={`denom-${testId}`}
        className="flex-1 min-h-[48px] text-xl font-mono text-center bg-white border-2"
      />
      <div className="w-20 text-right font-mono text-sm text-slate-500">€{subtotal}</div>
    </div>
  );
}

function ExtraInput({ label, v, setV, testId, locked = false }) {
  return (
    <div>
      <Label className={`text-[10px] uppercase tracking-widest font-bold ${locked ? "text-emerald-700" : "text-slate-500"}`}>{label}</Label>
      <Input
        type="number"
        inputMode="decimal"
        step="0.01"
        value={v}
        onChange={(e) => !locked && setV(e.target.value)}
        readOnly={locked}
        placeholder="0.00"
        data-testid={testId}
        className={`mt-1 min-h-[52px] text-xl font-mono font-bold text-center border-2 ${
          locked ? "bg-emerald-50 border-emerald-300 text-emerald-900 cursor-not-allowed" : ""
        }`}
        title={locked ? "Auto-populated from Wolt webhook · read-only" : undefined}
      />
      {locked && (
        <div className="text-[10px] text-emerald-700 mt-0.5 font-mono">🔒 auto · live webhook</div>
      )}
    </div>
  );
}

function ScanBox({ label, icon, onPick, parsed, busy, testId }) {
  return (
    <label className={`block border-2 border-dashed rounded-xl p-4 cursor-pointer ${busy ? "border-slate-900 bg-slate-100" : "border-slate-300 hover:border-slate-500"}`}>
      <input type="file" accept="image/*" capture="environment" onChange={onPick} className="hidden" data-testid={`${testId}-input`} />
      <div className="flex items-center gap-2 text-slate-700 font-bold">
        {icon}
        <span>{busy ? "Scanning…" : label}</span>
      </div>
      {parsed && <div className="text-xs text-emerald-700 font-mono mt-2">{parsed}</div>}
    </label>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 min-w-[140px]" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className={`font-mono text-2xl sm:text-3xl font-bold mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}


function PriceAuditPanel({ shift, items, zReport }) {
  const [localItems, setLocalItems] = useState(items);
  useEffect(() => setLocalItems(items), [items]);

  const updatePos = async (id, value) => {
    try {
      const { data } = await api.patch(`/shift-invoices/${id}`, {
        pos_price: value === "" || value == null ? null : Number(value),
      });
      setLocalItems((prev) => prev.map((i) => (i.id === id ? data : i)));
    } catch {
      toast.error("Failed to save POS price");
    }
  };

  if (!localItems || localItems.length === 0) {
    return (
      <Card className="p-6 my-6 border-dashed border-2" data-testid="price-audit-empty">
        <div className="flex items-center gap-3 text-slate-500">
          <Tag className="h-5 w-5" />
          <div>
            <div className="text-sm font-bold uppercase tracking-widest">Price-Tag Audit</div>
            <div className="text-xs">No products were priced through the calculator this shift.</div>
          </div>
        </div>
      </Card>
    );
  }

  const mismatched = localItems.filter(
    (i) => typeof i.pos_price === "number" && Math.abs(i.pos_price - i.retail_price) >= 0.05
  );
  const unset = localItems.filter((i) => i.pos_price == null);
  const matched = localItems.length - mismatched.length - unset.length;

  const totalCalcRevenue = localItems.reduce(
    (s, i) => s + (typeof i.pos_price === "number" ? i.pos_price : i.retail_price),
    0
  );
  const zReportTotal =
    zReport && (Number(zReport.total_sales) || (Number(zReport.cash_total) || 0) + (Number(zReport.card_total) || 0));

  return (
    <Card className="p-6 my-6 border-slate-200" data-testid="price-audit-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Price-Tag Audit
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Έλεγχος Τιμών Ραφιού · Compare calculated retail vs. actual POS sell price
            {zReport ? " (Z-Report scanned)" : " · scan Z-Report above to enable cross-check"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="audit-summary">
          <Pill tone="emerald" label="MATCHED" value={matched} icon={<Check className="h-3.5 w-3.5" />} />
          <Pill tone="rose" label="MISMATCH" value={mismatched.length} icon={<AlertTriangle className="h-3.5 w-3.5" />} />
          <Pill tone="slate" label="UNSET" value={unset.length} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3" data-testid="audit-calc-revenue">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Expected POS Revenue (sum of shelf prices)
          </div>
          <div className="font-mono text-2xl font-bold mt-1 text-slate-900">
            €{totalCalcRevenue.toFixed(2)}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3" data-testid="audit-zreport-total">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Z-Report Total Sales
          </div>
          <div className="font-mono text-2xl font-bold mt-1 text-slate-900">
            {zReportTotal != null && Number.isFinite(zReportTotal) ? `€${zReportTotal.toFixed(2)}` : "—"}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-slate-200">
              <th className="py-2 pr-3">Product</th>
              <th className="py-2 pr-3">Category</th>
              <th className="py-2 pr-3 text-right">Calc Retail</th>
              <th className="py-2 pr-3 text-right">POS Price</th>
              <th className="py-2 pr-3 text-right">Δ</th>
              <th className="py-2 pr-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {localItems.map((it) => (
              <AuditRow key={it.id} item={it} onPosChange={updatePos} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function AuditRow({ item, onPosChange }) {
  const [posInput, setPosInput] = useState(item.pos_price == null ? "" : String(item.pos_price));
  useEffect(() => {
    setPosInput(item.pos_price == null ? "" : String(item.pos_price));
  }, [item.pos_price]);

  const pos = posInput === "" ? null : Number(posInput);
  const delta = typeof pos === "number" && Number.isFinite(pos) ? pos - item.retail_price : null;
  const mismatched = delta !== null && Math.abs(delta) >= 0.05;
  const matched = delta !== null && !mismatched;

  const commit = () => {
    const newVal = posInput === "" ? null : Number(posInput);
    const oldVal = item.pos_price == null ? null : Number(item.pos_price);
    if (newVal === oldVal) return;
    if (newVal !== null && !Number.isFinite(newVal)) return;
    onPosChange(item.id, posInput);
  };

  return (
    <tr
      data-testid={`audit-row-${item.id}`}
      className={`border-b border-slate-100 ${mismatched ? "bg-rose-50" : ""}`}
    >
      <td className="py-2 pr-3 font-semibold text-slate-900">{item.product_name}</td>
      <td className="py-2 pr-3 text-slate-500 text-xs">{item.category || "—"}</td>
      <td className="py-2 pr-3 text-right font-mono font-bold text-blue-700">
        €{item.retail_price.toFixed(2)}
      </td>
      <td className="py-2 pr-3 text-right">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={posInput}
          onChange={(e) => setPosInput(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === "Enter" && commit()}
          placeholder="—"
          data-testid={`audit-pos-${item.id}`}
          className="h-9 text-sm font-mono text-right px-2 py-0 w-24 inline-block"
        />
      </td>
      <td className="py-2 pr-3 text-right font-mono font-bold">
        {delta === null ? (
          <span className="text-slate-300">—</span>
        ) : (
          <span className={mismatched ? "text-rose-700" : "text-emerald-700"}>
            {delta >= 0 ? "+" : ""}€{delta.toFixed(2)}
          </span>
        )}
      </td>
      <td className="py-2 pr-3">
        {mismatched ? (
          <span
            data-testid={`audit-status-mismatch-${item.id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider"
          >
            <AlertTriangle className="h-3 w-3" />
            Mismatch
          </span>
        ) : matched ? (
          <span
            data-testid={`audit-status-match-${item.id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider"
          >
            <Check className="h-3 w-3" />
            OK
          </span>
        ) : (
          <span
            data-testid={`audit-status-unset-${item.id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wider"
          >
            Unset
          </span>
        )}
      </td>
    </tr>
  );
}

function Pill({ tone, label, value, icon }) {
  const styles =
    tone === "emerald"
      ? "bg-emerald-100 text-emerald-700"
      : tone === "rose"
      ? "bg-rose-100 text-rose-700"
      : "bg-slate-100 text-slate-600";
  return (
    <div className={`px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 ${styles}`}>
      {icon}
      {value} {label}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Iter 49 · End-Shift Media Capture
// Native HTML5 MediaRecorder + photo file input. Uploads to
// /api/endshift/media which stamps URLs onto the shift doc — the
// existing Twilio WhatsApp dispatcher then attaches them automatically
// inside the SAVE EVERYTHING & CLOSE pipeline.
// ─────────────────────────────────────────────────────────────────────
function EndShiftMediaCapture({ shiftId }) {
  const [recState, setRecState] = useState("idle"); // idle | recording | ready
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(null);
  const mediaRecRef = React.useRef(null);
  const chunksRef = React.useRef([]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setRecState("ready");
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      setRecState("recording");
    } catch (e) {
      toast.error("Mic access denied · " + e.message);
    }
  };
  const stopRec = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
  };
  const onPhotoPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };
  const upload = async () => {
    if (!shiftId) {
      toast.error("No active shift");
      return;
    }
    if (!audioBlob && !photo) {
      toast.error("Record audio or attach a photo first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("shift_id", shiftId);
      if (audioBlob) fd.append("audio", audioBlob, "voice-note.webm");
      if (photo) fd.append("photo", photo);
      const { data } = await api.post("/endshift/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(data);
      toast.success("Media bundled · will attach to Twilio WhatsApp");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="p-4 border-2 border-violet-300 bg-violet-50/40 mb-6" data-testid="endshift-media-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-2xl">🎙️</div>
        <div>
          <div className="font-bold text-violet-900">Ηχογράφηση Φωνητικού + Φωτογραφία Ταμείου</div>
          <div className="text-xs text-violet-700">Bundled into the Twilio WhatsApp recap automatically</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {/* Voice */}
        <div className="bg-white border border-violet-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-widest font-bold text-violet-700 mb-2">
            🎙️ Record Voice Note
          </div>
          <div className="flex items-center gap-2">
            {recState !== "recording" ? (
              <Button
                onClick={startRec}
                size="sm"
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="endshift-mic-start"
              >
                <Mic className="h-4 w-4 mr-1" /> Record
              </Button>
            ) : (
              <Button
                onClick={stopRec}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white animate-pulse"
                data-testid="endshift-mic-stop"
              >
                <Square className="h-4 w-4 mr-1" /> Stop
              </Button>
            )}
            {audioUrl && (
              <audio
                src={audioUrl}
                controls
                className="h-8 flex-1"
                data-testid="endshift-audio-preview"
              />
            )}
          </div>
        </div>
        {/* Photo */}
        <div className="bg-white border border-violet-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-widest font-bold text-violet-700 mb-2">
            📸 Cash Register Photo
          </div>
          <div className="flex items-center gap-2">
            <label className="bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold px-3 py-1.5 rounded cursor-pointer flex items-center gap-1">
              <Camera className="h-4 w-4" /> Pick
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onPhotoPick}
                className="hidden"
                data-testid="endshift-photo-input"
              />
            </label>
            {photoPreview && (
              <img
                src={photoPreview}
                alt="register"
                className="h-12 w-12 object-cover rounded border border-slate-300"
                data-testid="endshift-photo-preview"
              />
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-3">
        {uploaded && (
          <span className="text-xs text-emerald-700 font-bold" data-testid="endshift-media-uploaded">
            ✅ Bundled
          </span>
        )}
        <Button
          onClick={upload}
          disabled={uploading || (!audioBlob && !photo)}
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="endshift-media-upload-btn"
        >
          <Upload className="h-4 w-4 mr-1" />
          {uploading ? "Uploading…" : "Bundle to WhatsApp"}
        </Button>
      </div>
    </Card>
  );
}


// Iter 68 — Native WhatsApp deep-link button. Fetches the same
// bilingual EN/EL Z-Report summary that the Twilio dispatcher would
// send, URL-encodes it, and opens `https://wa.me/<digits>?text=…`
// which the OS hands off to the installed WhatsApp app (mobile) or
// web.whatsapp.com (desktop). This is the manual delivery path the
// owner can ALWAYS use even when the Twilio sandbox can't deliver.
function WhatsAppDeepLinkButton({ shiftId, disabled }) {
  const [busy, setBusy] = React.useState(false);

  // Iter 81 — Pull each media URL as a Blob, build a File[], then call
  // navigator.share({text, files}) which the mobile OS hands off to
  // WhatsApp pre-attached. Returns an empty array if any fetch fails
  // (still allows the text-only fallback).
  const buildFiles = async (urls) => {
    if (!Array.isArray(urls) || urls.length === 0) return [];
    const out = [];
    for (let i = 0; i < urls.length; i += 1) {
      try {
        const absolute = /^https?:/.test(urls[i])
          ? urls[i]
          : `${process.env.REACT_APP_BACKEND_URL || ""}${urls[i]}`;
        const res = await fetch(absolute, { credentials: "omit" });
        if (!res.ok) continue;
        const blob = await res.blob();
        const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
        out.push(new File([blob], `kioskforce-shift-${i + 1}.${ext}`, {
          type: blob.type || "image/jpeg",
        }));
      } catch {
        /* skip failed fetch — still attempt remaining */
      }
    }
    return out;
  };

  const openWhatsApp = async () => {
    if (!shiftId) {
      toast.error("Shift not yet saved · save first then tap again");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get(`/endshift/${shiftId}/whatsapp-link`);
      if (!data?.wa_url) {
        toast.error("Could not build WhatsApp link");
        return;
      }
      if (!data.phone) {
        toast.warning("Owner phone not set in Settings · WhatsApp opens for manual pick");
      } else if (data.fallback_used) {
        toast.info("Owner phone empty · using fallback recipient");
      }

      // Iter 81 — When the browser supports navigator.canShare({files})
      // (modern mobile Chrome/Safari) AND the shift has at least one
      // media asset, bundle them with the text and open the native share
      // sheet so the operator picks WhatsApp and everything lands in
      // one message. Otherwise fall back to the text-only wa.me deep-link.
      const mediaUrls = Array.isArray(data.media_urls) ? data.media_urls : [];
      if (
        navigator.share &&
        mediaUrls.length > 0 &&
        typeof navigator.canShare === "function"
      ) {
        const files = await buildFiles(mediaUrls);
        const sharePayload = {
          title: "KioskForce ERP · End Shift",
          text: data.summary,
          ...(files.length ? { files } : {}),
        };
        if (files.length && navigator.canShare({ files })) {
          try {
            await navigator.share(sharePayload);
            toast.success(`📤 Bundled ${files.length} photo${files.length === 1 ? "" : "s"} + summary`);
            return;
          } catch (shareErr) {
            // User cancelled OR share rejected files — fall back to wa.me.
            if (shareErr && shareErr.name !== "AbortError") {
              toast.info("Native share unavailable · opening WhatsApp text-only");
            }
          }
        }
      }

      // Fallback path — text-only wa.me deep-link.
      window.open(data.wa_url, "_blank", "noopener,noreferrer");
      if (mediaUrls.length > 0) {
        toast.info(`📎 ${mediaUrls.length} photo(s) ready — attach manually after the text loads`);
      }
    } catch (e) {
      // Iter 96 — Graceful Fallback. If the WhatsApp launcher itself
      // throws (popup blocker, locked-down browser, mobile OS denial)
      // we swallow the amber warning. The summary + short link are
      // already in the operator's clipboard (above), so they have a
      // calm paste-fallback.
      if (typeof console !== "undefined" && console.debug) {
        console.debug("[Iter 96] WhatsApp launcher fallback:", e);
      }
      toast.success(
        "✓ Summary ready · paste it in WhatsApp when ready",
        { id: "endshift-iter96-share-fallback" }
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      onClick={openWhatsApp}
      disabled={busy || disabled}
      data-testid="endshift-whatsapp-deeplink-btn"
      title="Opens native WhatsApp pre-filled with the full Z-Report summary"
      className="w-full min-h-[72px] bg-emerald-600 hover:bg-emerald-700 text-white text-lg sm:text-xl font-bold uppercase tracking-[0.18em] rounded-xl shadow-lg flex flex-col items-center justify-center gap-1 disabled:bg-slate-400 disabled:cursor-not-allowed disabled:opacity-80"
    >
      <span className="flex items-center gap-2">
        <span aria-hidden>🟢</span>
        <span>{busy ? "Building link…" : "ΑΠΟΣΤΟΛΗ ΜΕΣΩ WHATSAPP · SEND VIA WHATSAPP APP"}</span>
      </span>
      <span className="text-[10px] opacity-80 normal-case tracking-wide">
        Opens native WhatsApp pre-filled with the EN/EL Z-Report summary
      </span>
    </Button>
  );
}
